import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Wallet, Lock, Shield, TrendingUp, TrendingDown, Minus,
  Crown, ArrowUpRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inr = (n) =>
  `₹${Number(Math.round(Number(n) || 0)).toLocaleString("en-IN")}`;
const crore = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(v % 1e7 === 0 ? 0 : 2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return inr(v);
};

const RANK_LABEL = {
  super_stockist: "Super-Stockist",
  wholesaler: "Wholesaler",
  dealer: "Dealer",
  booking_agent: "Booking Agent",
  introducer: "Introducer",
  custodian: "Custodian",
  guarantor: "Guarantor",
  seller: "Authorised Seller",
};

/**
 * VinciTerminalHero — "Glass Cockpit" hero panel for /account (buyer + seller).
 *
 * Frosted-glass card over an animated obsidian aura. Live 5151-series /
 * credit-utilisation gauge in the centre, four holographic tiles (Wallet,
 * Rank, Credit Line, Sponsor) around it, and a bottom Price Pulse ticker
 * pinned to the frame. No admin gating — admin already gets the full
 * Executive Command dashboard at /admin/command.
 */
export function VinciTerminalHero({ api }) {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [apps, setApps] = useState([]);
  const [sponsor, setSponsor] = useState(null);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const [w, c, r] = await Promise.all([
          api.get("/wallet/balance").catch(() => ({ data: null })),
          api.get("/credit-applications/mine").catch(() => ({ data: null })),
          api.get("/rates").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setWallet(w.data);
        setApps(c.data?.applications || []);
        setRates(Array.isArray(r.data) ? r.data : (r.data?.rates || []));
      } catch { /* silent */ }
      // sponsor is only meaningful for dependent sub-roles
      if (user?.parent_id) {
        try {
          const r = await api.get("/my-sponsor");
          if (alive) setSponsor(r.data);
        } catch { /* silent */ }
      }
    })();
    return () => { alive = false; };
  }, [api, user]);

  const approvedCredit = useMemo(() => {
    const row = (apps || []).find((a) => a.status === "approved");
    return row ? Number(row.approved_limit_inr || row.requested_limit_inr || 0) : 0;
  }, [apps]);

  // ── Gauge value · credit utilisation % if a credit line exists,
  // otherwise fall back to the buyer's position in the 5151-series
  // (last 4 digits of Vinci Code · 0-9999 → 0-100%).
  const gauge = useMemo(() => {
    if (approvedCredit > 0 && wallet) {
      const used = Math.max(0, approvedCredit - Number(wallet.balance || 0));
      const pct = Math.min(100, Math.round((used / approvedCredit) * 100));
      return {
        pct,
        label: `${pct}%`,
        caption: "Credit utilised",
        sub: `${crore(used)} of ${crore(approvedCredit)}`,
      };
    }
    const seq = (user?.vendor_code || "").slice(-4);
    const n = Number.parseInt(seq, 10);
    if (Number.isFinite(n)) {
      const pct = Math.min(100, Math.round((n / 9999) * 100));
      return {
        pct,
        label: user?.vendor_code || "—",
        caption: "5151-Series position",
        sub: "Awaiting credit allotment",
      };
    }
    return { pct: 0, label: "—", caption: "Awaiting allotment",
             sub: "Complete KYC to unlock" };
  }, [approvedCredit, wallet, user]);

  const rank = useMemo(() => {
    const cat = (user?.buyer_category || user?.account_type || "").toLowerCase();
    if (cat === "seller" || user?.account_type === "seller")
      return { label: "Authorised Seller",
                sub: user?.verified ? "Trading Active" : "Under Verification" };
    return {
      label: RANK_LABEL[cat] || "Partner",
      sub: user?.verified ? "Trading Active" : "Verification Pending",
    };
  }, [user]);

  const sponsorLine = useMemo(() => {
    if (sponsor?.business_name) {
      return {
        label: sponsor.business_name,
        sub: sponsor.vendor_code || "Linked Sponsor",
      };
    }
    return { label: "Direct", sub: "No Sponsor" };
  }, [sponsor]);

  const tickerRates = useMemo(() => {
    if (!rates?.length) return [];
    return rates
      .filter((r) => r.price)
      .slice(0, 12);
  }, [rates]);

  if (!user) return null;

  return (
    <section
      data-testid="vinci-terminal-hero"
      aria-label="Vinci Terminal — live operations cockpit"
      className="relative mt-6 mb-14 rounded-2xl overflow-hidden isolate"
      style={{
        background:
          "radial-gradient(1200px 500px at 20% 0%, rgba(184,115,51,0.10), transparent 60%),"
          + "radial-gradient(900px 400px at 90% 100%, rgba(47,185,153,0.08), transparent 60%),"
          + "linear-gradient(180deg,#08080a 0%,#050506 100%)",
        boxShadow:
          "inset 0 0 0 1px rgba(216,178,62,0.18),"
          + "inset 0 0 120px rgba(0,0,0,0.6),"
          + "0 40px 80px -30px rgba(0,0,0,0.7)",
      }}
    >
      {/* Digital-lines backdrop (pure CSS/SVG · zero payload) */}
      <DigitalLinesBackdrop />

      {/* Kicker */}
      <div className="relative flex items-center justify-center pt-6">
        <span
          data-testid="terminal-kicker"
          className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#2FB999] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2FB999]" />
          </span>
          Authorized Executive Terminal
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#D8B23E] opacity-60 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#D8B23E]" />
          </span>
        </span>
      </div>

      {/* Main frosted grid */}
      <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_1.15fr_1fr] gap-5 px-6 md:px-10 py-8">
        {/* Left column · Wallet + Credit */}
        <div className="flex flex-col gap-4">
          <HoloTile
            testid="terminal-tile-wallet"
            to="/wallet"
            kicker="Vinci Wallet"
            value={wallet ? inr(wallet.balance || 0) : "—"}
            sub={
              !wallet
                ? "Syncing…"
                : wallet.inducted
                  ? (wallet.can_trade ? "Trade-Active · Cyan Pulse" : "Below ₹500 floor")
                  : (wallet.induction_status === "pending"
                      ? "Induction under review"
                      : "Locked · Apply for access")
            }
            Icon={wallet && !wallet.inducted ? Lock : Wallet}
            tone={wallet?.inducted ? "cyan" : "gold"}
            pulse={wallet?.inducted && wallet?.can_trade}
          />
          <HoloTile
            testid="terminal-tile-credit"
            to="/account#credit-limit-section"
            kicker="Credit Line"
            value={approvedCredit > 0 ? crore(approvedCredit) : "Awaiting"}
            sub={
              approvedCredit > 0
                ? "Working-Capital · Approved"
                : (apps.length ? "Application under review"
                                 : "Apply for the ₹80 Cr programme")
            }
            Icon={Crown}
            tone="gold"
          />
        </div>

        {/* Centre column · Gauge */}
        <div className="flex flex-col items-center justify-center py-2">
          <div
            data-testid="terminal-user-strip"
            className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82] mb-1"
          >
            Vinci Terminal · Online
          </div>
          <div className="font-serif text-3xl md:text-4xl text-[#F5F5F0] tracking-tight text-center">
            {user?.name?.split(" ")[0] || "Partner"}
          </div>
          <div className="mt-4">
            <StateOfTradeGauge pct={gauge.pct} />
          </div>
          <div
            data-testid="terminal-gauge-caption"
            className="mt-3 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A]"
          >
            {gauge.caption}
          </div>
          <div className="font-mono text-[13px] text-[#F5F5F0] mt-1">
            {gauge.label}
          </div>
          <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">
            {gauge.sub}
          </div>
        </div>

        {/* Right column · Rank + Sponsor */}
        <div className="flex flex-col gap-4">
          <HoloTile
            testid="terminal-tile-rank"
            to="/account#your-orders"
            kicker="Vinci Rank"
            value={rank.label}
            sub={rank.sub}
            Icon={Shield}
            tone={user?.verified ? "cyan" : "gold"}
          />
          <HoloTile
            testid="terminal-tile-sponsor"
            to={user?.parent_id ? "/account#my-custodian-card" : "/register"}
            kicker="Sponsor"
            value={sponsorLine.label}
            sub={sponsorLine.sub}
            Icon={Shield}
            tone="neutral"
          />
        </div>
      </div>

      {/* Price Pulse ticker */}
      <div
        data-testid="terminal-price-pulse"
        className="relative border-t border-white/[0.07] bg-black/40 backdrop-blur-md"
      >
        <div className="flex items-center gap-3 px-4 py-2">
          <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#2FB999] flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2FB999] opacity-70 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2FB999]" />
            </span>
            Market Pulse
          </span>
          <div className="flex-1 min-w-0">
            {tickerRates.length ? (
              <Marquee speed={40} gradient={false} pauseOnHover autoFill>
                {tickerRates.map((r, i) => (
                  <PulseChip key={`${r.label}-${i}`} rate={r} />
                ))}
              </Marquee>
            ) : (
              <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#6f6f68]">
                Fetching live rates…
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function HoloTile({ testid, to, kicker, value, sub, Icon, tone = "gold", pulse = false }) {
  const toneMap = {
    gold:  { border: "rgba(216,178,62,0.32)", glow: "rgba(216,178,62,0.28)", ink: "#D8B23E" },
    cyan:  { border: "rgba(47,185,153,0.32)", glow: "rgba(47,185,153,0.28)", ink: "#2FB999" },
    neutral:{ border: "rgba(255,255,255,0.14)", glow: "rgba(255,255,255,0.10)", ink: "#C9C9C2" },
  };
  const t = toneMap[tone] || toneMap.gold;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      className="relative rounded-xl group"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
        border: `1px solid ${t.border}`,
        backdropFilter: "blur(14px)",
      }}
    >
      <Link
        to={to}
        data-testid={testid}
        className="block px-5 py-4 relative overflow-hidden rounded-xl"
      >
        {/* Bioluminescent hover halo */}
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"
          style={{
            boxShadow: `inset 0 0 40px ${t.glow}, 0 0 30px -6px ${t.glow}`,
          }}
        />
        {pulse && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{
              boxShadow: `inset 0 0 24px ${t.glow}`,
              animation: "vinciCyanPulse 3.6s ease-in-out infinite",
            }}
          />
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="font-grotesk text-[9px] uppercase tracking-[0.28em]"
              style={{ color: t.ink }}
            >
              {kicker}
            </div>
            <div className="font-serif text-[22px] md:text-[26px] text-[#F5F5F0] mt-2 truncate">
              {value}
            </div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-0.5 truncate">
              {sub}
            </div>
          </div>
          <div
            className="grid place-items-center w-9 h-9 rounded-lg shrink-0"
            style={{
              border: `1px solid ${t.border}`,
              background: "rgba(0,0,0,0.35)",
              color: t.ink,
            }}
          >
            <Icon size={15} />
          </div>
        </div>
        <span className="mt-3 inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82] group-hover:text-[#D8B23E] transition-colors">
          Open <ArrowUpRight size={11} />
        </span>
      </Link>
    </motion.div>
  );
}

function StateOfTradeGauge({ pct }) {
  const size = 200;
  const stroke = 8;
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  const arc = (pct / 100) * c * 0.75; // three-quarter dial for aesthetics
  const totalArc = c * 0.75;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}
            className="rotate-[135deg]" aria-hidden>
        <defs>
          <linearGradient id="goldSweep" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#7A5410" />
            <stop offset="50%" stopColor="#D8B23E" />
            <stop offset="100%" stopColor="#F5CE5C" />
          </linearGradient>
          <filter id="softGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke}
          strokeDasharray={`${totalArc} ${c - totalArc}`}
          strokeLinecap="round"
        />
        {/* Filled arc */}
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="url(#goldSweep)" strokeWidth={stroke}
          strokeLinecap="round"
          filter="url(#softGlow)"
          initial={{ strokeDasharray: `0 ${c}` }}
          animate={{ strokeDasharray: `${arc} ${c - arc}` }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      {/* Centre dot */}
      <div className="absolute inset-0 grid place-items-center">
        <div
          className="w-2.5 h-2.5 rounded-full bg-[#D8B23E]"
          style={{ boxShadow: "0 0 24px rgba(216,178,62,0.9)" }}
        />
      </div>
      {/* Tick marks */}
      <div className="absolute inset-0 grid place-items-center">
        <div className="relative w-[176px] h-[176px]">
          {[...Array(9)].map((_, i) => {
            const a = -180 + (i * 45); // spans 0-360 in tick space
            return (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 origin-[0_84px] bg-white/10"
                style={{
                  width: 1, height: 6,
                  transform: `rotate(${a}deg) translateY(-84px)`,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PulseChip({ rate }) {
  const up = rate.trend === "up";
  const down = rate.trend === "down";
  const color = up ? "#2FB999" : down ? "#C0654E" : "#8A8A82";
  const TrendIcon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span
      className="inline-flex items-center gap-2 mx-5 whitespace-nowrap"
      title={`${rate.label} · ${rate.change_pct}% change`}
    >
      <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
        {(rate.brand ? `${rate.brand} · ` : "") + (rate.label || rate.category)}
      </span>
      <span className="font-mono text-[11px] text-[#F5F5F0]">
        ₹{Number(rate.price || 0).toLocaleString("en-IN")}
      </span>
      <span
        className="font-mono text-[10px] inline-flex items-center gap-1"
        style={{ color }}
      >
        <TrendIcon size={10} />
        {rate.change_pct != null ? `${Math.abs(rate.change_pct)}%` : "—"}
      </span>
    </span>
  );
}

function DigitalLinesBackdrop() {
  const [videoOK, setVideoOK] = useState(true);
  return (
    <>
      <style>{`
        @keyframes vinciCyanPulse {
          0%, 100% { box-shadow: inset 0 0 24px rgba(47,185,153,0.16); }
          50%      { box-shadow: inset 0 0 44px rgba(47,185,153,0.42); }
        }
        @keyframes vinciSweep {
          0%   { transform: translateX(-20%); opacity: 0; }
          20%  { opacity: 0.8; }
          100% { transform: translateX(120%); opacity: 0; }
        }
      `}</style>
      {/* Video backdrop · own-domain served, 146KB WebM, poster fallback
          on <video> failure keeps the aesthetic intact if the asset can't
          be decoded (older Safari, low-power mode, etc). */}
      {videoOK ? (
        <video
          data-testid="terminal-video-backdrop"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.42] pointer-events-none"
          src="/assets/obsidian-grid.webm"
          poster="/assets/obsidian-grid-poster.png"
          autoPlay muted loop playsInline preload="auto"
          onError={() => setVideoOK(false)}
          aria-hidden
        />
      ) : (
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full opacity-[0.14] pointer-events-none"
          preserveAspectRatio="none"
          viewBox="0 0 1200 480"
        >
          <defs>
            <pattern id="vinciGrid" width="48" height="48"
                      patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none"
                    stroke="rgba(216,178,62,0.4)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="1200" height="480" fill="url(#vinciGrid)" />
        </svg>
      )}
      {/* Obsidian tint over the video to keep the gold aura + tiles legible */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,5,6,0.55) 0%, rgba(5,5,6,0.72) 100%)",
        }}
      />
      {/* Slow gold sweep */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -left-1/3 w-2/3 h-[140%]"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(216,178,62,0.10), transparent)",
          transform: "skewX(-18deg)",
          animation: "vinciSweep 14s ease-in-out infinite",
        }}
      />
    </>
  );
}
